//================================================================
//  Component: AddEditCostPane
//================================================================

//  Purpose: This side pane allows a user to add a cost to the Total Contract Value table

//  Properties:
//    - paneOpen = { useState, holds a boolean value that opens and closes the sidePane}
//    - setPaneOpen = { useState, updates the boolean value that opens and closes the sidePane}
//    - task = { useState, stores the currently selected request}

//  Example:
//  
//    <AddCostPane
//      paneOpen={openPane}
//      setPaneOpen={setOpenPane}
//      task={task}
//    ></AddCostPane>

//================================================================

// Libraries
import React, { useState, useContext } from 'react';
import { useForm } from 'react-hook-form';

// Contexts
import { GetUser } from '../../../../../../Library/GlobalContexts';

// Functions
import AddRemoveDocumentArray from '../../../../../../Library/AddRemoveDocumentArray';
import WriteDocument from '../../../../../../Library/WriteDocument';

// Components

// Images
import Loading from '../../../../../../Components/Images/Image_Loading_Ripple.svg';


export default function AddEditCostPane({
    paneOpen,
    setPaneOpen,
    task,
    costData
}) {

    //------------------------------------------------------
    //  useContexts
    //------------------------------------------------------

    const getUser = useContext(GetUser);

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Used to set pane status > 'onload', 'error'
    const [paneStatus, setPaneStatus] = useState('onload');

    // --------------------------------------------------
    //  Form State
    // --------------------------------------------------

    const { register, watch, handleSubmit, reset, formState: { errors } } = useForm();

    const termsSelected = watch('costtype', paneOpen === 'edit' ? costData.costtype : '');

    // --------------------------------------------------
    //  Functions
    // --------------------------------------------------

    // Function to check if the form is valid
    const isFormComplete = () => {

        const description = watch('description');
        const costType = watch('costtype');
        const terms = watch('terms');
        const costPerTerm = watch('costperterm');

        // Check if all required fields are filled based on cost type
        const isValid =
            description && // 'Description' required
            costType && // 'Term' required
            (
                ((costType === 'Monthly' || costType === 'Yearly') && terms) ||
                (costType === 'One-time')
            ) &&
            (costPerTerm >= 0) && // Allow 0 totals
            !Object.keys(errors).length; // Check for any errors

        return isValid;
    };

    // Submit Handler
    const onSubmit = (data) => {

        setPaneStatus('pending');

        // Add cost -> add data to document array
        if (paneOpen === 'add') {

            // Write cost to Firestore
            const costDoc = {
                'costid': Date.now(),
                'description': data?.description,
                'costtype': data?.costtype,
                'terms': data?.costtype === 'Monthly' || data?.costtype === 'Yearly' ? +data?.terms : null,
                'costperterm': +data?.costperterm ?? null,
                'onetimecost': +data?.onetimecost ?? null,
            };

            const modifiedBy = {
                'email': getUser.emailaddress,
                'givenname': getUser.givenname,
                'surname': getUser.surname,
            };

            return AddRemoveDocumentArray('requests', task.requestid, 'costs', costDoc, 'add', modifiedBy)
                .then(() => {
                    reset();
                    setPaneStatus('onload');
                    setPaneOpen('');

                }).catch((err) => {
                    console.log(err);
                    reset();
                    setPaneStatus('error');
                    setPaneOpen('');

                })
        }

        // Edit cost -> WriteDocument with updated cost list
        else {

            const newCostList = [];

            task?.costs?.forEach((cost) => {
                if (cost?.costid !== costData?.costid) newCostList.push(cost);
            })

            const updatedCost = {
                'costid': Date.now(),
                'description': data?.description,
                'costtype': data?.costtype,
                'terms': data?.costtype === 'Monthly' || data?.costtype === 'Yearly' ? +data?.terms : null,
                'costperterm': +data?.costperterm ?? null,
                'onetimecost': +data?.onetimecost ?? null,
            };

            newCostList.push(updatedCost)

            WriteDocument('requests', task.requestid, { 'costs': newCostList }, true)
                .then(() => {
                    reset();
                    setPaneStatus('onload');
                    setPaneOpen('');
                }).catch((err) => {
                    console.log(err);
                    reset();
                    setPaneStatus('error');
                    setPaneOpen('');
                })

        }

    }

    // --------------------------------------------------
    //  HTML
    // --------------------------------------------------

    // ==================================================
    //  Hide Side Pane
    // ==================================================

    if (!paneOpen) return null;

    // ==================================================
    //  Show Side Pane  
    // ==================================================

    // ---------------------------------------------------
    //  Onload
    // ---------------------------------------------------
    if (paneStatus === 'onload') {
        return (
            <div className='Pane-Background'>
                <dialog className='Pane-Container'>

                    {/* Form */}
                    <form className='grid grid-rows-[80px_1fr_70px] items-center justify-between w-full overflow-hidden' onSubmit={handleSubmit(onSubmit)}>

                        {/* Header */}
                        <div className='flex flex-col gap-2 p-4'>
                            <h4 className='text-[22px] p-0 m-0'> {`${paneOpen === 'add' ? 'Add a cost to ' : 'Edit a cost in '}the Total Contract Value`}</h4>
                        </div>

                        {/* ===================================== */}
                        {/*  Body                                 */}
                        {/* ===================================== */}

                        {/* Form */}
                        <div className='w-[50vw] h-full flex flex-col gap-3 overflow-y-auto p-4 border border-solid shadow-inner'>

                            {/* Description */}
                            <div className='Form-Row'>

                                <label className='font-medium mb-3'> Description <span className='text-[#C4314B]'>*</span> </label>

                                <input
                                    className={errors.description ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    {...register('description', {
                                        required: 'This field is required'
                                    })}
                                    placeholder='E.g. Monthly subscription fee'
                                    type='text'
                                    defaultValue={paneOpen === 'edit' ? costData?.description : ''}
                                ></input>

                                {errors.description && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.description?.message}</p>}

                            </div>

                            {/* Term */}
                            <div className='Form-Row'>

                                <label className='font-medium mb-3' > Term <span className='text-[#C4314B]'>*</span> </label>
                                <p className='italic font-light'>If this cost has a term, then enter the duration in months or years. Select one-time fee if it has no term.</p>

                                <select className='Input-Field-Select'
                                    {...register('costtype', {
                                        required: 'This field is required'
                                    })}
                                >
                                    <option value='One-time' selected={paneOpen === 'edit' && costData.costtype === 'One-time'}> One-time fee </option>
                                    <option value='Monthly' selected={paneOpen === 'edit' && costData.costtype === 'Monthly'}> Monthly </option>
                                    <option value='Yearly' selected={paneOpen === 'edit' && costData.costtype === 'Yearly'}> Yearly </option>

                                </select>

                                {errors.costtype && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.costtype?.message}</p>}

                            </div>

                            {(termsSelected === 'Monthly' || termsSelected === 'Yearly') && (

                                <div className='Form-Row'>

                                    <label className='font-medium mb-3'> Term Duration <span className='text-[#C4314B]'>*</span> </label>


                                    <div className='flex flex-row gap-2'>

                                        {/* Number of terms */}
                                        <input
                                            className={errors.terms ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                            {...register('terms', {
                                            })}
                                            style={{ width: '80px' }}
                                            type='number'
                                            defaultValue={paneOpen === 'edit' ? costData?.terms : ''}
                                        ></input>

                                        <label>{termsSelected === 'Monthly' ? 'month(s)' : 'year(s)'}</label>

                                        {errors.terms && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.terms?.message}</p>}

                                    </div>

                                </div>

                            )}

                            {/* Cost */}
                            <div className='Form-Row'>
                                <label className='font-medium mb-3'> Cost per term <span className='text-[#C4314B]'>*</span> </label>
                                <p className='italic font-light'>Please enter in AUD. Excluding Tax.</p>
                                <div>
                                    <input
                                        className={errors.costperterm ? 'Input-Field-Text-Error' : 'Input-Field-Text'}
                                        {...register('costperterm', {
                                            required: 'This field is required',
                                            valueAsNumber: true,
                                            setValueAs: v => parseFloat(parseFloat(v).toFixed(2))
                                        })}
                                        type='number'
                                        step='0.01'
                                        defaultValue={paneOpen === 'edit' ? parseFloat(costData?.costperterm)?.toFixed(2) : ''}
                                    ></input>

                                    {errors.costperterm && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.costperterm?.message}</p>}
                                </div>
                            </div>

                            {/* Currency (Read-Only) */}
                            <div className='Form-Row'>
                                <label className='font-medium mb-3'> Currency </label>
                                <input className='Input-Field-Text' disabled value={'AUD'}></input>

                            </div>

                        </div>


                        {/* ===================================== */}
                        {/*  Footer                               */}
                        {/* ===================================== */}

                        {/* Buttons */}
                        <div className='px-4 py-3'>
                            <div className='flex flex-row gap-3'>

                                {/* Add/Save */}
                                <button className='Primary-Button' type='submit' value='submit' disabled={!isFormComplete()}>
                                    {paneOpen === 'add' ? 'Add' : 'Save'}
                                </button>

                                {/* Cancel */}
                                <button className='Secondary-Button' onClick={() => {

                                    // Reset Form and Close Side Pane
                                    setPaneOpen('');
                                    reset();

                                }}>
                                    Cancel
                                </button>

                            </div>
                        </div>
                    </form>

                </dialog >
            </div >
        );
    }

    // ---------------------------------------------------
    //  Pending
    // ---------------------------------------------------

    else if (paneStatus === 'pending') {
        return (
            <div className='Pane-Background'>
                <dialog className='Pane-Container flex justify-center items-center text-center'>
                    <img alt='loading-circle-icon' src={Loading}></img>
                </dialog>
            </div>
        )

    }

    // ---------------------------------------------------
    //  Error
    // ---------------------------------------------------

    else if (paneStatus === 'error') {
        return (
            <div className='Pane-Background'>

                {/* Pane Container */}
                <dialog className='Pane-Container'>
                    <div className='flex flex-col gap-2 w-full justify-center items-center'>

                        <img className='my-2 w-[100px]' src={Error} alt='Error'></img>
                        <h4 className='mb-0'>Oops! Something went wrong.</h4>
                        <p className='text-center leading-[1.7]'>
                            An error occurred while we processed your request.
                        </p>
                        <button
                            className='Primary-Button'
                            onClick={() => {
                                setPaneStatus('onload');
                                setPaneOpen('');
                            }}
                        >
                            Close
                        </button>

                    </div>
                </dialog>
            </div>

        )
    }


}
